import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Checkbox,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Close,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  AccessTime,
  Flag,
  Person,
  CalendarToday,
} from '@mui/icons-material';
import { Task, TaskStatus, TaskPriority, TaskType, Epic } from '../../types/common';
import { SubtaskStatus } from '../../api/types/SubtaskTypes';
import { TaskTimer } from '../TimeTracking/TaskTimer';
import { TimeEntries } from '../TimeTracking/TimeEntries';
import { TaskAttachments } from '../Attachments/TaskAttachments';
import { ActivityFeed } from '../Activity/ActivityFeed';
import { EntityType } from '../../api/types/ActivityTypes';
import { fetchTaskComments, createComment } from '../../store/slices/commentsSlice';
import {
  fetchTaskSubtasks,
  createSubtask,
  updateSubtask,
  deleteSubtask,
} from '../../store/slices/subtasksSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useBoardMembers } from '../../hooks/useBoardMembers';
import { showToast } from '../../store/uiSlice';
import { ListRowSkeleton } from '../common/Skeletons';
import './TaskDetail.scss';

const PRIORITY_ACCENTS: Record<string, string> = {
  urgent: '#f0576b',
  high: '#fb8332',
  medium: '#f5b942',
  low: '#22c58c',
};

interface TaskDetailProps {
  task: Task;
  open: boolean;
  onClose: () => void;
  // May resolve a boolean (true = succeeded) so callers like the assignee
  // picker below can show a targeted success toast without double-firing
  // an error toast the caller already reported (e.g. BoardPage/SprintBoard's
  // handleUpdateTask reverts the optimistic patch and toasts on failure
  // itself, then returns false).
  onUpdate: (taskId: string, updates: Partial<Task>) => void | Promise<void | boolean>;
  epics?: Epic[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} className="task-detail__tab-panel">
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const TaskDetail: React.FC<TaskDetailProps> = ({ task, open, onClose, onUpdate, epics = [] }) => {
  const dispatch = useAppDispatch();
  const { members: boardMembers, loading: loadingMembers } = useBoardMembers(task.boardId);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState<Task>(task);
  const [newComment, setNewComment] = useState('');
  const [newSubtask, setNewSubtask] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [savingAssignee, setSavingAssignee] = useState(false);
  const [postingComment, setPostingComment] = useState(false);

  const comments = useAppSelector((state) => state.comments.commentsByTask[task.id] || []);
  const subtasks = useAppSelector((state) => state.subtasks.subtasksByTask[task.id] || []);
  const commentsLoading = useAppSelector((state) => state.comments.loading) && comments.length === 0;
  const subtasksLoading = useAppSelector((state) => state.subtasks.loading) && subtasks.length === 0;

  useEffect(() => {
    dispatch(fetchTaskComments(task.id));
    dispatch(fetchTaskSubtasks(task.id));
  }, [dispatch, task.id]);

  const assignedMember = boardMembers.find((member) => String(member.id) === task.assigneeId);
  const assigneeDisplayName = !task.assigneeId
    ? 'Unassigned'
    : assignedMember
      ? assignedMember.fullName || assignedMember.userName
      : loadingMembers
        ? 'Loading…'
        : 'Former member';

  const handleAssigneeChange = async (rawValue: string) => {
    setEditingAssignee(false);
    // Explicit null (not undefined) so the PATCH actually clears assigneeId
    // instead of the key being dropped from the JSON body and silently ignored.
    const newAssigneeId = rawValue || null;
    if (newAssigneeId === (task.assigneeId ?? null)) return;

    const newAssigneeName = newAssigneeId
      ? boardMembers.find((m) => String(m.id) === newAssigneeId)?.fullName ||
      boardMembers.find((m) => String(m.id) === newAssigneeId)?.userName
      : null;

    setSavingAssignee(true);
    try {
      // onUpdate (BoardPage/SprintBoard's handleUpdateTask) already reverts
      // the optimistic change and shows its own error toast on failure,
      // resolving `false` - only show a success toast when it didn't.
      const result = await onUpdate(task.id, { assigneeId: newAssigneeId });
      if (result !== false) {
        dispatch(
          showToast({
            message: newAssigneeName
              ? `Assigned to ${newAssigneeName}`
              : 'Assignee removed',
            type: 'success',
          })
        );
      }
    } catch (error: any) {
      console.error('Failed to update assignee:', error);
      dispatch(
        showToast({
          message:
            (typeof error === 'string' ? error : error?.message) ||
            'Failed to update assignee. Please try again.',
          type: 'error',
        })
      );
    } finally {
      setSavingAssignee(false);
    }
  };

  const handleSave = async () => {
    try {
      await onUpdate(task.id, editedTask);
      setIsEditing(false);
    } catch (error: any) {
      console.error('Failed to save task:', error);
      dispatch(showToast({
        message: error.message || 'Failed to save task. Please try again.',
        type: 'error',
      }));
    }
  };

  const handleCancel = () => {
    setEditedTask(task);
    setIsEditing(false);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setPostingComment(true);
    try {
      await dispatch(
        createComment({ taskId: task.id, data: { content: newComment } }),
      ).unwrap();
      setNewComment('');
    } catch (error: any) {
      dispatch(showToast({
        message: error?.message || 'Failed to post comment. Please try again.',
        type: 'error',
      }));
    } finally {
      setPostingComment(false);
    }
  };

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return;

    try {
      await dispatch(
        createSubtask({ taskId: task.id, data: { title: newSubtask } }),
      ).unwrap();
      setNewSubtask('');
    } catch (error: any) {
      dispatch(showToast({
        message: error?.message || 'Failed to add subtask. Please try again.',
        type: 'error',
      }));
    }
  };

  const handleToggleSubtaskDone = async (subtaskId: string, currentStatus: SubtaskStatus) => {
    const status = currentStatus === SubtaskStatus.DONE ? SubtaskStatus.TODO : SubtaskStatus.DONE;
    try {
      await dispatch(updateSubtask({ taskId: task.id, subtaskId, data: { status } })).unwrap();
    } catch (error: any) {
      dispatch(showToast({
        message: error?.message || 'Failed to update subtask. Please try again.',
        type: 'error',
      }));
    }
  };

  const handleSubtaskAssigneeChange = async (subtaskId: string, rawValue: string) => {
    const assigneeId = rawValue ? Number(rawValue) : null;
    try {
      await dispatch(updateSubtask({ taskId: task.id, subtaskId, data: { assigneeId } })).unwrap();
    } catch (error: any) {
      dispatch(showToast({
        message: error?.message || 'Failed to update subtask assignee. Please try again.',
        type: 'error',
      }));
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    try {
      await dispatch(deleteSubtask({ taskId: task.id, subtaskId })).unwrap();
    } catch (error: any) {
      dispatch(showToast({
        message: error?.message || 'Failed to delete subtask. Please try again.',
        type: 'error',
      }));
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT:
        return 'error';
      case TaskPriority.HIGH:
        return 'warning';
      case TaskPriority.MEDIUM:
        return 'info';
      case TaskPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.DONE:
        return 'success';
      case TaskStatus.IN_PROGRESS:
        return 'primary';
      case TaskStatus.REVIEW:
        return 'info';
      case TaskStatus.BLOCKED:
        return 'error';
      default:
        return 'default';
    }
  };

  const completedSubtasks = subtasks.filter((st) => st.status === SubtaskStatus.DONE).length;
  const totalSubtasks = subtasks.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="task-detail-dialog"
    >
      <DialogTitle
        className="task-detail__header"
        style={{ ['--priority-accent' as any]: PRIORITY_ACCENTS[task.priority] || PRIORITY_ACCENTS.medium }}
      >
        <Box className="task-detail__header-content">
          {/* Left: key stacked above title */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {task.key && (
              <Typography
                variant="caption"
                color="text.secondary"
                className="task-detail__key"
                sx={{ fontWeight: 600, display: 'block' }}
              >
                {task.key}
              </Typography>
            )}
            <Typography variant="h6" className="task-detail__title">
              {isEditing ? (
                <TextField
                  fullWidth
                  value={editedTask.title}
                  onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                  variant="standard"
                  className="task-detail__title-input"
                />
              ) : (
                task.title
              )}
            </Typography>
          </Box>

          {/* Right: action buttons */}
          <Box className="task-detail__actions">
            {isEditing ? (
              <>
                <IconButton onClick={handleSave} color="primary">
                  <Save />
                </IconButton>
                <IconButton onClick={handleCancel}>
                  <Cancel />
                </IconButton>
              </>
            ) : (
              <IconButton onClick={() => setIsEditing(true)}>
                <Edit />
              </IconButton>
            )}
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className="task-detail__content">
        <Box className="task-detail__body">
          {/* Left Column - Main Content */}
          <Box className="task-detail__main">
            {/* Description */}
            <Box className="task-detail__section">
              <Typography variant="subtitle2" className="task-detail__section-title">
                Description
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={editedTask.description || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                  placeholder="Add a description..."
                  variant="outlined"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {task.description || 'No description provided.'}
                </Typography>
              )}
            </Box>

            {/* Subtasks */}
            <Box className="task-detail__section">
              <Typography variant="subtitle2" className="task-detail__section-title">
                Subtasks ({completedSubtasks}/{totalSubtasks})
              </Typography>
              <List className="task-detail__subtasks">
                {subtasksLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
                    <ListRowSkeleton height={36} />
                    <ListRowSkeleton height={36} />
                  </Box>
                )}
                {subtasks.map((subtask, i) => (
                  <ListItem
                    key={subtask.id}
                    className="task-detail__subtask stagger-item"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <Checkbox
                      checked={subtask.status === SubtaskStatus.DONE}
                      onChange={() => handleToggleSubtaskDone(subtask.id, subtask.status)}
                    />
                    <ListItemText
                      primary={subtask.key ? `${subtask.key} · ${subtask.title}` : subtask.title}
                      className={
                        subtask.status === SubtaskStatus.DONE
                          ? 'task-detail__subtask--completed'
                          : ''
                      }
                    />
                    <FormControl size="small" sx={{ minWidth: 140, mr: 1 }}>
                      <Select
                        displayEmpty
                        value={subtask.assigneeId ? String(subtask.assigneeId) : ''}
                        onChange={(e) =>
                          handleSubtaskAssigneeChange(subtask.id, e.target.value)
                        }
                        variant="standard"
                      >
                        <MenuItem value="">
                          <em>Unassigned</em>
                        </MenuItem>
                        {boardMembers.map((member) => (
                          <MenuItem key={member.id} value={String(member.id)}>
                            {member.fullName || member.userName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSubtask(subtask.id)}
                      aria-label="Delete subtask"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
              <Box className="task-detail__add-subtask">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a subtask..."
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                />
                <IconButton onClick={handleAddSubtask} color="primary">
                  <Add />
                </IconButton>
              </Box>
            </Box>

            {/* Time Tracking Section */}
            <Box className="task-detail__section" sx={{ mt: 3 }}>
              <TaskTimer taskId={task.id} />
              <Box sx={{ mt: 2 }}>
                <TimeEntries taskId={task.id} />
              </Box>
            </Box>

            {/* Tabs for Comments, Activity, and Attachments */}
            <Box className="task-detail__tabs">
              <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
                <Tab label={`Comments (${comments.length})`} />
                <Tab label="Activity" />
                <Tab label="Attachments" />
              </Tabs>

              <TabPanel value={currentTab} index={0}>
                <Box className="task-detail__comments">
                  {commentsLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 0.5 }}>
                      <ListRowSkeleton height={56} />
                      <ListRowSkeleton height={56} />
                    </Box>
                  )}
                  <List>
                    {comments.map((comment, i) => (
                      <ListItem
                        key={comment.id}
                        alignItems="flex-start"
                        className="task-detail__comment stagger-item"
                        style={{ animationDelay: `${i * 40}ms` }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            {(comment.user?.fullName || comment.user?.userName || 'U').charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box className="task-detail__comment-header">
                              <Typography variant="subtitle2">
                                {comment.user?.fullName || comment.user?.userName || 'Unknown user'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(comment.createdAt).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                          secondary={comment.content}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box className="task-detail__add-comment">
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      variant="outlined"
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || postingComment}
                      sx={{ mt: 1 }}
                    >
                      {postingComment ? 'Posting…' : 'Add Comment'}
                    </Button>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={currentTab} index={1}>
                <ActivityFeed entityType={EntityType.TASK} entityId={task.id} limit={50} />
              </TabPanel>

              <TabPanel value={currentTab} index={2}>
                <TaskAttachments taskId={task.id} />
              </TabPanel>
            </Box>
          </Box>

          {/* Right Column - Metadata */}
          <Box className="task-detail__sidebar">
            {/* Status */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editedTask.status}
                    onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value as TaskStatus })}
                  >
                    {Object.values(TaskStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={task.status.replace('_', ' ')}
                  color={getStatusColor(task.status)}
                  size="small"
                />
              )}
            </Box>

            {/* Priority */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                <Flag fontSize="small" /> Priority
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editedTask.priority}
                    onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value as TaskPriority })}
                  >
                    {Object.values(TaskPriority).map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority?.toUpperCase() || priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={task.priority?.toUpperCase() || 'MEDIUM'}
                  color={getPriorityColor(task.priority)}
                  size="small"
                />
              )}
            </Box>

            {/* Type */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Type
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editedTask.type || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, type: e.target.value as TaskType })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {Object.values(TaskType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type?.toUpperCase() || type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={task.type?.toUpperCase() || 'None'}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Epic */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Epic
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editedTask.epicId || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, epicId: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {epics.map((epic) => (
                      <MenuItem key={epic.id} value={epic.id}>
                        {epic.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={epics.find(e => e.id === task.epicId)?.name || 'None'}
                  size="small"
                  sx={{
                    backgroundColor: epics.find(e => e.id === task.epicId)?.color || '#6c757d',
                    color: 'white',
                  }}
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Assignee */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                <Person fontSize="small" /> Assigned To
              </Typography>
              {editingAssignee ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={task.assigneeId || ''}
                    open
                    autoFocus
                    disabled={savingAssignee}
                    onClose={() => setEditingAssignee(false)}
                    onChange={(e) => handleAssigneeChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {boardMembers.map((member) => (
                      <MenuItem key={member.id} value={String(member.id)}>
                        {member.fullName || member.userName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box
                  onClick={() => setEditingAssignee(true)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 0.5,
                    cursor: savingAssignee ? 'default' : 'pointer',
                    opacity: savingAssignee ? 0.6 : 1,
                    '&:hover': savingAssignee ? undefined : { opacity: 0.75 },
                  }}
                >
                  {task.assigneeId && (
                    <Avatar sx={{ width: 22, height: 22, fontSize: 12 }}>
                      {assigneeDisplayName.charAt(0).toUpperCase()}
                    </Avatar>
                  )}
                  <Typography variant="body2">{assigneeDisplayName}</Typography>
                </Box>
              )}
            </Box>
            {/* Reporter */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                <Person fontSize="small" /> Reporter
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 0.5,
                }}
              >
                {(task as any).reporter && (
                  <Avatar sx={{ width: 22, height: 22, fontSize: 12 }}>
                    {((task as any).reporter?.fullName || (task as any).reporter?.userName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <Typography variant="body2">
                  {(task as any).reporter?.fullName ||
                    (task as any).reporter?.userName ||
                    (task.reporterId ? `User #${task.reporterId}` : 'Unknown')}
                </Typography>
              </Box>
            </Box>

            {/* Created By - Read Only */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                <Person fontSize="small" /> Created By
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 0.5,
                }}
              >
                {(() => {
                  // Get creator info from createdBy relation or boardMembers fallback
                  const createdByUser = task.createdBy ||
                    (task.createdById
                      ? boardMembers.find((m) => String(m.id) === String(task.createdById))
                      : null);
                  const creatorName = createdByUser
                    ? ((createdByUser as any).fullName || (createdByUser as any).userName || null)
                    : (task.createdById ? `User #${task.createdById}` : 'Unknown');

                  return (
                    <>
                      {createdByUser && (
                        <Avatar sx={{ width: 22, height: 22, fontSize: 12, bgcolor: 'primary.main' }}>
                          {(creatorName || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                      <Typography variant="body2">{creatorName || 'Unknown'}</Typography>
                    </>
                  );
                })()}
              </Box>
            </Box>

            {/* Due Date */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                <CalendarToday fontSize="small" /> Due Date
              </Typography>
              {isEditing ? (
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={editedTask.dueDate ? editedTask.dueDate.split('T')[0] : ''}
                  onChange={(e) => setEditedTask({ ...editedTask, dueDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Typography variant="body2">
                  {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}
                </Typography>
              )}
            </Box>

            {/* Estimated Hours */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                <AccessTime fontSize="small" /> Estimated Hours
              </Typography>
              {isEditing ? (
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={editedTask.estimatedHours || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, estimatedHours: Number(e.target.value) })}
                />
              ) : (
                <Typography variant="body2">
                  {task.estimatedHours || '-'} h
                </Typography>
              )}
            </Box>

            {/* Actual Hours */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Actual Hours
              </Typography>
              <Typography variant="body2">
                {task.actualHours || 0} h
              </Typography>
            </Box>

            {/* Story Points */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Story Points
              </Typography>
              {isEditing ? (
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={editedTask.storyPoints || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, storyPoints: Number(e.target.value) })}
                />
              ) : (
                <Typography variant="body2">
                  {task.storyPoints || '-'}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Created */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">
                {new Date(task.createdAt).toLocaleDateString()}
              </Typography>
            </Box>

            {/* Updated */}
            <Box className="task-detail__meta-item">
              <Typography variant="caption" color="text.secondary">
                Updated
              </Typography>
              <Typography variant="body2">
                {new Date(task.updatedAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetail;
